// SupportInfo.js
// ---------------------------------------------------------
// Abschnitt 5: Hilfe & Support
// Zeigt Versionsinformationen, Link zum Handbuch,
// und ermöglicht das direkte Kontaktieren des Supports.
// ---------------------------------------------------------

/* global Office */

import { useState, useEffect } from "react";
import { FiHelpCircle, FiBookOpen, FiMail } from "react-icons/fi";
import { useLicenseContext } from "../contexts/LicenseContext";
import { fetchBackendVersion } from "../api/versionApi";
import { useI18n } from "../contexts/I18nContext";

const appVersion = require("../../package.json").version;

export default function SupportInfo() {
  const { locale, t, formatDateTime, formatTime } = useI18n();
  const { license } = useLicenseContext();
  const [currentTime, setCurrentTime] = useState("");
  const [contactLoading, setContactLoading] = useState(false);

  const supportMail = "info@avamboo.de";
  const handbuchUrl =
    "https://www.avamboo.de/downloads/avamboo-encrypt/AvambooEncrypt-Handbuch.pdf";

  // Determine environment
  // Both Outlook Web and New Outlook (Desktop) report PlatformType.OfficeOnline.
  // They cannot be reliably distinguished via Office.js alone.
  // We use the hostname as a hint: known Outlook Web domains → "Outlook Web",
  // otherwise → "New Outlook (Desktop)".
  // On localhost (dev) this will always fall through to "New Outlook (Desktop)" — that's acceptable.
  const isOutlookHost = Office?.context?.host === Office?.HostType?.Outlook;
  const platform = Office?.context?.platform;
  const isOfficeOnlinePlatform =
    platform === Office?.PlatformType?.OfficeOnline;
  const isOutlookWebDomain =
    /outlook\.(live|office|office365)\.(com|net)/i.test(
      window.location.hostname,
    );
  const environment = !isOutlookHost
    ? t("Unbekannt")
    : platform === Office?.PlatformType?.Mac
      ? t("New Outlook für Mac")
      : isOfficeOnlinePlatform && isOutlookWebDomain
        ? t("Outlook Web")
        : isOfficeOnlinePlatform
          ? t("New Outlook (Desktop)")
          : t("Unbekannt");

  // Set current time on component mount
  useEffect(() => {
    const now = new Date();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzShort = formatTime(now, { timeZoneName: "short" })
      .split(" ")
      .pop();
    const formatted = `${formatDateTime(now, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })} (${tzShort || tz})`;
    setCurrentTime(formatted);
  }, [formatDateTime, formatTime]);

  const openHandbuch = () => {
    window.open(handbuchUrl, "_blank");
  };

  const contactSupport = async () => {
    setContactLoading(true);
    const subject = encodeURIComponent(t("Avamboo Encrypt – Supportanfrage"));
    const now = new Date();
    const tzShort = now
      .toLocaleTimeString(locale, { timeZoneName: "short" })
      .split(" ")
      .pop();
    const timestamp = formatDateTime(now, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }) + ` (${tzShort})`;
    const lizenzTyp = license?.lizenzTyp || t("Unbekannt");
    const userEmail =
      Office?.context?.mailbox?.userProfile?.emailAddress ||
      "[bitte eintragen]";

    let backendVersion = t("Unbekannt");
    try {
      backendVersion = await fetchBackendVersion();
    } catch (_) {}

    setContactLoading(false);

    const body = encodeURIComponent(
      `${t("Avamboo Encrypt – Supportanfrage")}

${t("Version (Frontend): {version}", { version: appVersion })}
${t("Version (Backend):  {version}", { version: backendVersion })}
${t("Umgebung: {environment}", { environment })}
${t("Zeitpunkt: {timestamp}", { timestamp })}
${t("Lizenz: {license}", { license: lizenzTyp })}
${t("Benutzer E-Mail-Adresse: {email}", { email: userEmail })}

--------------------------------------------------
${t("Bitte beschreiben Sie Ihr Anliegen hier:")}



${t("(Tipp: Eine kurze Beschreibung und ein Screenshot helfen uns,\ndas Problem schneller zu verstehen und zu lösen.)")}
`,
    );

    window.location.href = `mailto:${supportMail}?subject=${subject}&body=${body}`;
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "10px",
        border: "1px solid #e0e0e0",
        boxShadow: "0 4px 16px rgba(100,100,100,0.18)",
        padding: "16px 14px",
      }}
    >
      <h3 style={{ marginBottom: "16px" }}>
        <FiHelpCircle
          size={16}
          style={{ marginRight: "6px", verticalAlign: "middle" }}
        />
        {t("Hilfe & Support")}
      </h3>

      <table
        style={{
          width: "100%",
          marginBottom: "16px",
          fontSize: "13px",
          color: "#555",
        }}
      >
        <tbody>
          <tr>
            <td
              style={{ paddingRight: "12px", fontWeight: "500", color: "#444" }}
            >
              {t("Umgebung:")}
            </td>
            <td>{environment}</td>
          </tr>
          <tr>
            <td
              style={{ paddingRight: "12px", fontWeight: "500", color: "#444" }}
            >
              {t("Zeitpunkt:")}
            </td>
            <td>{currentTime}</td>
          </tr>
          <tr>
            <td
              style={{ paddingRight: "12px", fontWeight: "500", color: "#444" }}
            >
              {t("Version:")}
            </td>
            <td>{appVersion}</td>
          </tr>
        </tbody>
      </table>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginTop: "14px",
        }}
      >
        <button
          onClick={openHandbuch}
          style={{
            padding: "10px 24px",
            fontSize: "14px",
            fontWeight: "600",
            background: "#0078d7",
            color: "#fff",
            borderRadius: "6px",
            border: "none",
            cursor: "pointer",
            width: "100%",
          }}
        >
          <FiBookOpen
            size={15}
            style={{ marginRight: "6px", verticalAlign: "middle" }}
          />
          {t("PDF-Handbuch öffnen")}
        </button>

        <button
          onClick={contactSupport}
          disabled={contactLoading}
          style={{
            padding: "10px 24px",
            fontSize: "14px",
            fontWeight: "600",
            background: "#0078d7",
            opacity: contactLoading ? 0.7 : 1,
            color: "#fff",
            borderRadius: "6px",
            border: "none",
            cursor: "pointer",
            width: "100%",
          }}
        >
          <FiMail
            size={15}
            style={{ marginRight: "6px", verticalAlign: "middle" }}
          />
          {contactLoading ? t("Wird vorbereitet...") : t("Support kontaktieren")}
        </button>
      </div>

      <p
        style={{
          fontSize: "0.9em",
          color: "#555",
          marginTop: "16px",
        }}
      >
        {t("Bei Problemen oder Fragen wenden Sie sich bitte direkt an den Avamboo Support.")}
      </p>
    </div>
  );
}
